import React from "react";
import { useNavigate } from "react-router-dom";
import type { Product } from "../types";
import { useCart } from "../hooks/useCart";
import {
  ProductCard as StyledProductCard,
  ProductThumb,
  ProductInfo,
  ProductName,
  ProductPrice,
  Button,
} from "./styled";

interface ProductCardProps {
  product: Product;
}

/**
 * Thumbnails are rendered locally rather than fetched (see ProductThumb).
 * Deterministic per product id so every browser in the demo wall shows the
 * same catalogue in the same colours.
 */
const GLYPHS = ["🎧", "👕", "⌚", "🎒", "☕", "📱", "🕶️", "🔋"];
const glyphFor = (id: number) => GLYPHS[(id - 1) % GLYPHS.length];
const hueFor = (id: number) => (id * 47) % 360;

export const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const { addItem } = useCart();
  const navigate = useNavigate();

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    addItem(product);
  };

  const handleCardClick = () => {
    navigate(`/product/${product.id}`);
  };

  return (
    <StyledProductCard onClick={handleCardClick}>
      <ProductThumb $hue={hueFor(product.id)} aria-label={product.name}>
        {glyphFor(product.id)}
      </ProductThumb>
      <ProductInfo>
        <ProductName>{product.name}</ProductName>
        <ProductPrice>${product.price.toFixed(2)}</ProductPrice>
        {/* No description on the card: a two-line product name pushed it past
            the card's overflow:hidden and clipped it mid-line, and card heights
            came out uneven across a grid row. The description is still on the
            product detail page. */}
        <Button onClick={handleAddToCart}>Add to Cart</Button>
      </ProductInfo>
    </StyledProductCard>
  );
};
